import React, { useState } from 'react';
import {
  requestPasswordOtp,
  verifyPasswordOtp,
  type FranchiseRecoveryIdentity,
} from '../api/recovery.api';
import styles from './PasswordRecoveryModal.module.css';

type Step = 'identity' | 'otp' | 'done';

interface Props {
  onClose: () => void;
}

export default function PasswordRecoveryModal({ onClose }: Props) {
  const [step, setStep] = useState<Step>('identity');
  const [franchiseName, setFranchiseName] = useState('');
  const [ownerName, setOwnerName] = useState('');
  const [phone, setPhone] = useState('');
  const [otp, setOtp] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [newPasswordConfirm, setNewPasswordConfirm] = useState('');
  const [maskedPhone, setMaskedPhone] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const getIdentity = (): FranchiseRecoveryIdentity => ({
    franchiseName: franchiseName.trim(),
    ownerName: ownerName.trim(),
    phone: phone.replace(/[-\s]/g, ''),
  });

  const handleRequestOtp = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    const id = getIdentity();
    if (!id.franchiseName) {
      setError('프랜차이즈 이름을 입력해주세요.');
      return;
    }
    if (!id.ownerName) {
      setError('대표자 이름을 입력해주세요.');
      return;
    }
    if (!/^0\d{9,10}$/.test(id.phone)) {
      setError('휴대폰번호는 01012345678 형식으로 입력해주세요.');
      return;
    }

    setIsSubmitting(true);
    try {
      const { maskedPhone: p } = await requestPasswordOtp(id);
      setMaskedPhone(p);
      setStep('otp');
    } catch (err) {
      setError(err instanceof Error ? err.message : '인증 요청에 실패했습니다.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleVerifyOtp = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    if (otp.length !== 6) {
      setError('6자리 인증번호를 입력해주세요.');
      return;
    }
    if (newPassword.length < 8) {
      setError('새 비밀번호는 8자 이상이어야 합니다.');
      return;
    }
    if (newPassword !== newPasswordConfirm) {
      setError('새 비밀번호가 일치하지 않습니다.');
      return;
    }
    setIsSubmitting(true);
    try {
      await verifyPasswordOtp(getIdentity(), otp.trim(), newPassword);
      setStep('done');
    } catch (err) {
      setError(err instanceof Error ? err.message : '인증에 실패했습니다.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className={styles.overlay} onClick={onClose}>
      <div className={styles.modal} onClick={(e) => e.stopPropagation()}>
        <h2 className={styles.title}>비밀번호 재설정</h2>

        {step === 'identity' && (
          <form onSubmit={handleRequestOtp} className={styles.form}>
            <p className={styles.desc}>
              아래 <b>3가지 정보가 모두 일치</b>해야 인증번호를 발송합니다.
            </p>
            <div className={styles.field}>
              <label className={styles.label}>프랜차이즈 이름</label>
              <input
                type="text"
                className={styles.input}
                value={franchiseName}
                onChange={(e) => setFranchiseName(e.target.value)}
                placeholder="가입 시 등록한 이름"
                disabled={isSubmitting}
                autoFocus
              />
            </div>
            <div className={styles.field}>
              <label className={styles.label}>대표자 이름</label>
              <input
                type="text"
                className={styles.input}
                value={ownerName}
                onChange={(e) => setOwnerName(e.target.value)}
                placeholder="대표자 성명"
                disabled={isSubmitting}
              />
            </div>
            <div className={styles.field}>
              <label className={styles.label}>휴대폰번호</label>
              <input
                type="tel"
                className={styles.input}
                value={phone}
                onChange={(e) => setPhone(e.target.value.replace(/[^\d-]/g, ''))}
                placeholder="01012345678"
                maxLength={13}
                disabled={isSubmitting}
                autoComplete="tel"
              />
            </div>
            {error && <div className={styles.error}>{error}</div>}
            <div className={styles.btnRow}>
              <button
                type="button"
                className={styles.btnSecondary}
                onClick={onClose}
                disabled={isSubmitting}
              >
                취소
              </button>
              <button
                type="submit"
                className={styles.btnPrimary}
                disabled={isSubmitting}
              >
                {isSubmitting ? '확인 중...' : '인증번호 발송'}
              </button>
            </div>
            <p className={styles.helpText}>
              정보를 모두 기억하지 못하시나요? 랜딩페이지 <b>문의하기</b>에서
              신분증 본인확인 후 복구 가능합니다.
            </p>
          </form>
        )}

        {step === 'otp' && (
          <form onSubmit={handleVerifyOtp} className={styles.form}>
            <p className={styles.desc}>
              <b>{maskedPhone}</b>로 6자리 인증번호를 발송했습니다. 5분 이내에 입력해주세요.
            </p>
            <div className={styles.field}>
              <label className={styles.label}>인증번호</label>
              <input
                type="text"
                inputMode="numeric"
                className={styles.input}
                value={otp}
                onChange={(e) => setOtp(e.target.value.replace(/\D/g, '').slice(0, 6))}
                placeholder="6자리 숫자"
                disabled={isSubmitting}
                maxLength={6}
                autoFocus
              />
            </div>
            <div className={styles.field}>
              <label className={styles.label}>새 비밀번호</label>
              <input
                type="password"
                className={styles.input}
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                placeholder="8자 이상"
                disabled={isSubmitting}
                autoComplete="new-password"
              />
            </div>
            <div className={styles.field}>
              <label className={styles.label}>새 비밀번호 확인</label>
              <input
                type="password"
                className={styles.input}
                value={newPasswordConfirm}
                onChange={(e) => setNewPasswordConfirm(e.target.value)}
                placeholder="비밀번호 다시 입력"
                disabled={isSubmitting}
                autoComplete="new-password"
              />
            </div>
            {error && <div className={styles.error}>{error}</div>}
            <div className={styles.btnRow}>
              <button
                type="button"
                className={styles.btnSecondary}
                onClick={() => setStep('identity')}
                disabled={isSubmitting}
              >
                이전
              </button>
              <button
                type="submit"
                className={styles.btnPrimary}
                disabled={isSubmitting}
              >
                {isSubmitting ? '확인 중...' : '비밀번호 재설정'}
              </button>
            </div>
          </form>
        )}

        {step === 'done' && (
          <div className={styles.form}>
            <p className={styles.desc} style={{ textAlign: 'center', padding: '16px 0' }}>
              ✅ 비밀번호가 재설정되었습니다.<br />
              새 비밀번호로 로그인해주세요.
            </p>
            <button type="button" className={styles.btnPrimary} onClick={onClose}>
              로그인 화면으로
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
